"use client";

import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatCurrency, formatDate, formatTime } from "@/lib/utils";
import { Appointment, Customer, Vehicle, Staff } from "@/types";
import { PaymentOutput } from "@/types/payment.types";
import { Edit, Trash2, DollarSign, Crown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { getBookingTypePresentation } from "@/lib/bookingTypePresentation";
import { AppointmentEditDialog } from "./AppointmentEditDialog";
import { RecordPaymentDialog } from "./RecordPaymentDialog";
import { paymentService } from "@/services/paymentService";

interface AppointmentDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: Appointment | null;
  customers: Customer[];
  vehicles: Vehicle[];
  staff: Staff[];
  getStatusBadge: (status: string) => React.ReactElement;
}

export function AppointmentDetailDialog({
  open,
  onOpenChange,
  appointment,
  customers,
  vehicles,
  staff,
  getStatusBadge,
}: AppointmentDetailDialogProps) {
  const { deleteAppointment } = useApp();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [payment, setPayment] = useState<PaymentOutput | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Fetch payment data when dialog opens
  useEffect(() => {
    const fetchPayment = async () => {
      if (open && appointment) {
        setPaymentLoading(true);
        try {
          const paymentData = await paymentService.getPaymentByRefId(appointment.id);
          setPayment(paymentData);
        } catch (error) {
          console.error("Failed to fetch payment:", error);
          setPayment(null);
        } finally {
          setPaymentLoading(false);
        }
      }
    };

    fetchPayment();
  }, [open, appointment]);

  if (!appointment) return null;

  const customer = customers.find((c) => c.id === appointment.customerId);
  const appointmentVehicles = vehicles.filter((v) => appointment.vehicleIds?.includes(v.id));
  const assignedStaff = appointment.staffId ? staff.find((s) => s.id === appointment.staffId) : null;
  const bookingTypePresentation = getBookingTypePresentation(appointment.bookingType);

  const handleDelete = async () => {
    try {
      await deleteAppointment(appointment.id);
      setDeleteDialogOpen(false);
      onOpenChange(false);
      toast.success("Appointment deleted successfully");
    } catch (error) {
      // Error already handled in AppContext
    }
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Appointment Details</DialogTitle>
                <DialogDescription>
                  Appointment ID: {appointment.id}
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                {payment && payment.dueAmount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPaymentDialogOpen(true)}
                    disabled={paymentLoading}
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    Record Payment
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Customer Name</Label>
              <p>{customer?.name || "N/A"}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Mobile #</Label>
              <p>{customer?.phone || "N/A"}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Vehicle Details</Label>
              <p>
                {appointmentVehicles.length > 0
                  ? appointmentVehicles
                      .map((v) => `${v.make} ${v.model} (${v.plateNumber})`)
                      .join(", ")
                  : "N/A"}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Appointment Type</Label>
              <Badge variant="outline" className="capitalize">
                {(appointment.type || "").replace(/_/g, " ")}
              </Badge>
            </div>
            <div>
              <Label className="text-muted-foreground">Booking Type</Label>
              <div className="mt-1">
                <Badge
                  variant="outline"
                  className={bookingTypePresentation.badgeClassName}
                >
                  {bookingTypePresentation.label}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Schedule Date</Label>
              <p>{formatDate(appointment.date)}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Schedule Time</Label>
              <p>{formatTime(appointment.date)}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Status</Label>
              <div className="mt-1">{getStatusBadge(appointment.status)}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Staff</Label>
              <p>{assignedStaff?.name || "N/A"}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Payment Mode</Label>
              <p className="capitalize">{appointment.paymentMode}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Amount</Label>
              {appointment.amount !== null ? (
                <div className="space-y-1">
                  <p className="font-semibold flex items-center gap-1">
                    {(appointment.platinumDiscount ?? 0) > 0 && (
                      <Crown className="h-4 w-4 text-amber-500" title="Platinum Member Discount Applied" />
                    )}
                    {formatCurrency(
                      (appointment.amount ?? 0) -
                        (appointment.discount ?? 0) -
                        (appointment.platinumDiscount ?? 0)
                    )}
                    {(appointment.platinumDiscount ?? 0) > 0 && (
                      <span className="text-sm text-muted-foreground line-through font-normal ml-1">
                        {formatCurrency(appointment.amount ?? 0)}
                      </span>
                    )}
                  </p>
                  {(appointment.platinumDiscount ?? 0) > 0 && (
                    <p className="text-xs text-amber-600">
                      Platinum Discount (15%): -{formatCurrency(appointment.platinumDiscount ?? 0)}
                    </p>
                  )}
                </div>
              ) : (
                <p className="font-semibold">N/A</p>
              )}
            </div>
            {/* Rule ID - internal field, not shown to client
            {appointment.ruleId && (
              <div>
                <Label className="text-muted-foreground">Rule ID</Label>
                <p className="text-sm font-mono">{appointment.ruleId}</p>
              </div>
            )}
            */}
          </div>
          {appointment.address && (
            <div>
              <Label className="text-muted-foreground">Address</Label>
              <p className="text-sm">{appointment.address}</p>
            </div>
          )}
          {appointment.remarks && (
            <div className="border-t pt-4">
              <Label className="text-muted-foreground">Remarks</Label>
              <p className="text-sm mt-1 p-3 bg-muted rounded-md">{appointment.remarks}</p>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <AppointmentEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        appointment={appointment}
        customers={customers}
        vehicles={vehicles}
        staff={staff}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Appointment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this appointment? This action
              cannot be undone. The appointment with ID{" "}
              <strong>{appointment.id}</strong> will be
              permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Record Payment Dialog */}
      {payment && (
        <RecordPaymentDialog
          open={paymentDialogOpen}
          onOpenChange={setPaymentDialogOpen}
          paymentId={payment.id}
          dueAmount={payment.dueAmount}
          onSuccess={async () => {
            // Refresh payment data after successful payment
            const updatedPayment = await paymentService.getPaymentByRefId(appointment.id);
            setPayment(updatedPayment);
          }}
        />
      )}
    </>
  );
}
